import React, { useState, useEffect, FC, useCallback, useMemo, useRef } from 'react';
import { time } from 'uniqid';
import { Modal, Button, Stack, H2 } from '@manulife/mux';
import { get } from 'lodash';
import { MIL_SECOND_FACTOR, DAYJS_DIFF } from '@trip-ux/constants';
import { useActions, resetStore, ReduxStore } from '@trip-ux/redux-store';
import { useSelector } from 'react-redux';
import { Progress } from '../Progress';
import { dayjs, checkIfCoverMeOrManulifeEmployee, interpolateTemplate } from '@trip-ux/common';
import { useUserActivity } from './useUserActivity';

export interface IdleReminderProps {
  countDownSeconds: number;
  refresh: () => void;
  cancel: () => void;
  isOpen?: boolean;
  labels: {
    timedOut: string;
    remainingTimeout: string;
    cancel: string;
    refresh: string;
  };
}

const checkIfShouldClearStore = (sessionExpiration, shouldCheckSessionExpiry) =>
  (dayjs().isSameOrAfter(dayjs(sessionExpiration)) && shouldCheckSessionExpiry) || !shouldCheckSessionExpiry;

const checkIfShouldExpireSession = (isSessionExpired, countDown) => !isSessionExpired && countDown <= 0;

export const IdleReminder: FC<IdleReminderProps> = ({ countDownSeconds, cancel, refresh, labels, isOpen = false }) => {
  const { organizationCode, sessionIsExpired, sessionExpiration, debounceInterval } = useSelector(
    (store: ReduxStore) => ({
      organizationCode: get(store, 'config.options.organizationCode'),
      sessionIsExpired: Boolean(get(store, 'keepAlive.err')),
      sessionExpiration: get(store, 'keepAlive.sessionExpiration'),
      debounceInterval: get(store, 'config.options.debounceInterval', 10000)
    })
  );
  const [countDown, setCountDown] = useState(countDownSeconds);
  const [showSpinner, setShowSpinner] = useState(false);
  const [isModalOpen, setModalOpen] = useState(isOpen);
  const [intervalTimer, setIntervalTimer] = useState(null);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [modalShowInterval, setModalShowInterval] = useState(null);
  const modalId = useRef(time('session-')).current;

  // Use ref to store the latest refresh function to avoid recreating the callback
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;


  const handleRefreshClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur();
    setModalOpen(false);
    refreshRef.current();
  }, []);

  const initiateInterval = () => {
    clearInterval(modalShowInterval);
    const intervalTimerRef = setInterval(() => {
      setCountDown(dayjs(sessionExpiration).diff(dayjs(), DAYJS_DIFF.SECONDS));
    }, MIL_SECOND_FACTOR);
    setIntervalTimer(intervalTimerRef);
  };

  useEffect(() => {
    isModalOpen && initiateInterval();
  }, [isModalOpen]);

  // Safety net for MUX Modal scroll lock leak.
  // Track modal open state and clear overflow:hidden when modal closes.
  const prevModalOpenRef = useRef(isModalOpen);

  useEffect(() => {
    const html = document.documentElement;
    const wasOpen = prevModalOpenRef.current;

    // Modal just closed: if MUX left `overflow: hidden` behind, clear it.
    if (!isModalOpen && wasOpen && html.style.overflow === 'hidden') {
      html.style.overflow = '';
    }

    prevModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  // On unmount, clear overflow if our modal was open.
  useEffect(() => {
    return () => {
      if (!prevModalOpenRef.current){
        return;
      } 
      const html = document.documentElement;
      if (html.style.overflow === 'hidden') {
        html.style.overflow = '';
      }
    };
  }, []);

  useEffect(() => {
    if (checkIfShouldExpireSession(sessionIsExpired, countDown)) {
      setIsSessionExpired(true);
      clearInterval(intervalTimer);
    }
  }, [countDown, intervalTimer, isSessionExpired, modalShowInterval]);

  useEffect(() => {
    setCountDown(countDownSeconds);
    setModalOpen(isOpen);

    if (!isOpen && prevModalOpenRef.current) {
      const html = document.documentElement;
      if (html.style.overflow === 'hidden') {
        html.style.overflow = '';
      }
    }

    clearInterval(intervalTimer);

    const showModalCountdown = setInterval(() => {
      if (dayjs(sessionExpiration).isSameOrBefore(dayjs().add(countDownSeconds, DAYJS_DIFF.SECONDS))) {
        setModalOpen(true);
      }
    }, MIL_SECOND_FACTOR);
    setModalShowInterval(showModalCountdown);

    return () => {
      clearInterval(showModalCountdown);
    };
  }, [sessionExpiration]);

  const timerOn = countDown > 0;
  const actions = useActions({ resetStore });

  const clearStore = (shouldCheckSessionExpiry = false) => {
    if (checkIfShouldClearStore(sessionExpiration, shouldCheckSessionExpiry)) {
      setModalOpen(false);
      actions.resetStore();
      setShowSpinner(true);
      cancel();
    }
  };

  useEffect(() => {
    if (sessionIsExpired) {
      setShowSpinner(true);
      cancel();
    }
  }, [cancel, sessionIsExpired]);

  useEffect(() => {
    const clearStoreWithSession = () => clearStore(true);
    window.addEventListener('beforeunload', clearStoreWithSession);
    return () => {
      window.removeEventListener('beforeunload', clearStoreWithSession);
    };
  }, []);

  // Create a stable refresh callback that doesn't depend on the refresh prop
  const memoizedRefresh = useCallback(() => {
    refreshRef.current();
  }, []); // Empty dependency array makes this truly stable

  // Memoize the activity events array to prevent unnecessary re-renders
  const activityEvents = useMemo(() => ['keydown', 'mousedown'], []);

  useUserActivity(
    memoizedRefresh,
    {
      debounceInterval,
      activityEvents,
      isSessionExpired: sessionIsExpired || isSessionExpired
    }
  );

  const isCoverMeOrManulifeEmp = checkIfCoverMeOrManulifeEmployee(organizationCode);
  return (
    <>
      {isCoverMeOrManulifeEmp && showSpinner && <Progress backdrop />}
      <Modal
        onClose={isCoverMeOrManulifeEmp ? () => clearStore(false) : cancel}
        isOpen={isModalOpen}
        id={modalId}
        closeIconAriaLabel={labels.cancel}
        shouldCloseOnBackdropClick={false}
        shouldCloseWithEscapeKey={false}
        aria-label="Idle Reminder"
        aria-labelledby="session-reminder-header"
        aria-describedby="session-reminder-content">
        <Stack as="div" gap="16px" align="center">
          <H2 id="session-reminder-header" aria-live="polite" aria-atomic="true">
            {timerOn ? interpolateTemplate(labels.remainingTimeout, { timeRemaining: countDown }): labels.timedOut}
          </H2>

          <Stack as="div" direction="row" id="session-reminder-content">
            <Button
              id="session-reminder-cancel"
              onClick={isCoverMeOrManulifeEmp ? () => clearStore(false) : cancel}
              variant="secondary">
              {labels.cancel}
            </Button>

            {timerOn && (
              <Button id="session-reminder-refresh" onClick={handleRefreshClick} variant="primary">
                {labels.refresh}
              </Button>
            )}
          </Stack>
        </Stack>
      </Modal>
    </>
  );
};
IdleReminder.displayName = 'IdleReminder';
export default IdleReminder;
